/**
 * 版权归 Meta Platforms, Inc. 及其附属公司所有。
 *
 * 根据 Apache 许可证版本 2.0（"许可证"）授权；
 * 除非遵守许可证，否则您不得使用此文件。
 * 您可以在以下网址获取许可证副本：
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * 除非适用法律要求或书面同意，否则按“原样”分发软件
 * 不提供任何形式的保证或条件。
 * 请参阅许可证中管理权限和限制的特定语言。
 */
import { convertVideoFrameToImageData, findBoundingBox } from './ImageUtils';

function getCanvas(width, height, isOffscreen = false) {
    if (isOffscreen || typeof document === 'undefined') {
        return new OffscreenCanvas(width, height);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
}

export function drawFrame(ctx, frame, width, height) {
    if (ctx) {
        ctx.drawImage(frame, 0, 0, width, height);
    }
}

/**
 * 根据掩码和图像帧，获取裁剪到边界框的掩蔽图像。
 */
export function getThumbnailImageDataOld(mask, videoFrame) {
    const data = mask.data;
    if (!ArrayBuffer.isView(data) || !(data instanceof Uint8Array)) {
        return new ImageData(0, 0);
    }

    const frame = convertVideoFrameToImageData(videoFrame);
    if (!frame) {
        return new ImageData(0, 0);
    }

    const frameData = frame.data;
    const scaleX = frame.width / mask.shape[1];
    const scaleY = frame.height / mask.shape[0];
    const boundingBox = findBoundingBox();
    const transformedData = new Uint8ClampedArray(data.length * 4);

    for (let i = 0; i < data.length; i++) {
        const newX = Math.floor(i / mask.shape[0]);
        const newY = i % mask.shape[0];
        const transformedIndex = (newY * mask.shape[1] + newX) * 4;
        const frameDataIndex = (newY * mask.shape[1] * scaleY + newX * scaleX) * 4;

        transformedData[transformedIndex] = frameData[frameDataIndex];
        transformedData[transformedIndex + 1] = frameData[frameDataIndex + 1];
        transformedData[transformedIndex + 2] = frameData[frameDataIndex + 2];
        transformedData[transformedIndex + 3] = (data[i] && 255) || 0;

        boundingBox.process(newX, newY, data[i] > 0);
    }

    const rotatedData = new ImageData(transformedData, mask.shape[1], mask.shape[0]);

    return boundingBox.crop(rotatedData);
}

/**
 * 根据掩码、掩码渲染上下文和视频帧，获取裁剪到边界框的掩蔽图像。
 */
function getThumbnailImageData(mask, maskCtx, frameBitmap) {
    const x = mask.bounds[0][0];
    const y = mask.bounds[0][1];
    const w = mask.bounds[1][0] - mask.bounds[0][0];
    const h = mask.bounds[1][1] - mask.bounds[0][1];

    if (w <= 0 || h <= 0) {
        return null;
    }

    const thumbnailMaskData = maskCtx.getImageData(x, y, w, h);

    const canvas = new OffscreenCanvas(w, h);
    const ctx = canvas.getContext('2d');

    ctx.putImageData(thumbnailMaskData, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    ctx.drawImage(frameBitmap, x, y, w, h, 0, 0, w, h);

    return ctx.getImageData(0, 0, w, h);
}

export async function generateThumbnail(track, mask, frame, ctx) {
    const hasPoints = (track.points.length ?? 0) > 0;
    if (!hasPoints) {
        return;
    }
    const bitmap = await createImageBitmap(frame);
    const thumbnailImageData = getThumbnailImageData(mask, ctx, bitmap);

    bitmap.close();
    const thumbnailCanvas = await getCanvasFromImageData(thumbnailImageData);
    return thumbnailCanvas;
}

export async function getCanvasFromImageData(imageData) {
    if (!imageData) {
        return '';
    }

    const canvas = getCanvas(imageData.width, imageData.height);
    const ctx = canvas.getContext('2d');

    if (ctx === null) {
        return '';
    }

    if (ctx) {
        ctx.putImageData(imageData, 0, 0);
    }

    if (canvas instanceof OffscreenCanvas) {
        const blob = await canvas.convertToBlob();
        return new Promise(resolve => {
            const reader = new FileReader();
            reader.addEventListener(
                'load',
                () => {
                    const result = reader.result;
                    if (typeof result === 'string') {
                        resolve(result);
                    } else {
                        resolve('');
                    }
                },
                false,
            );
            reader.readAsDataURL(blob);
        });
    }
    return canvas;
}

export function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex);
    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
              a: result[4] != null ? parseInt(result[4], 16) : 128,
          }
        : { r: 255, g: 0, b: 0, a: 128 };
}

export function getPointInImage(event, canvas, normalized = false) {
    const rect = canvas.getBoundingClientRect();

    const matrix = new DOMMatrix();

    const elementCenter = new DOMPoint(canvas.clientWidth / 2, canvas.clientHeight / 2);
    const imageCenter = new DOMPoint(canvas.width / 2, canvas.height / 2);
    matrix.translateSelf(elementCenter.x - imageCenter.x, elementCenter.y - imageCenter.y);

    const scale = Math.min(canvas.clientWidth / canvas.width, canvas.clientHeight / canvas.height);
    matrix.scaleSelf(scale, scale, 1, imageCenter.x, imageCenter.y);

    const point = new DOMPoint(event.clientX - rect.left, event.clientY - rect.top);
    const imagePoint = matrix.inverse().transformPoint(point);

    const x = Math.max(Math.min(imagePoint.x, canvas.width), 0);
    const y = Math.max(Math.min(imagePoint.y, canvas.height), 0);

    if (normalized) {
        return [x / canvas.width, y / canvas.height];
    }
    return [x, y];
}
